import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Table } from '@amzn/awsui-components-react';

import {
    getInstructorTypesList,
    selectIsLoading,
    selectIsLoaded,
    selectAllInstructorTypes,
    selectError,
    updateInstructorType,
    selectSelectedInstructorType,
    setSelectedInstructorType,
    addInstructorType,
    selectCount,
    resetInstructorTypesSlice,
} from '../../../common/store/slices/instructorTypesSlice';
import {
    AdminBusinessDataFormInputType,
    AdminBusinessDataFormSchema,
} from '../../interfaces/adminBusinessDataFormSchema';
import { AdminBusinessDataSelectors } from '../../interfaces/adminBusinessDataSelectors';
import AdminBusinessData from '../AdminBusinessData';
import { formatStatus } from '../AdminBusinessData/AdminBusinessData.Status';
import { useNotifications } from '../../../common/context/grimsbyNotifications';
import handleBusinessDataNotification from '../../../common/utils/handleBusinessDataNotification';
import parseBoolean from '../../../common/utils/parseBoolean';
import { InstructorTypeItemData } from '../../../common/interfaces/businessDataItem/instructorTypeItem';

const INSTRUCTOR_TYPES_TABLE_TITLE = 'Instructor types';
const INSTRUCTOR_TYPES_DISPLAY_SINGULAR = 'Instructor type';
export const INSTRUCTOR_TYPES_NAME_KEY = 'instructor_type';

// this array must exclude the "name" column because
// we need to define it inside AdminBusinessData so we can attach an eventHandler to that link
export const columnDefinitions: Array<Table.ColumnDefinition> = [
    {
        id: 'active',
        header: 'Status',
        cell: (instructorType: InstructorTypeItemData) =>
            formatStatus(instructorType.active.toString()),
    },
];

/**
 * Editable form fields for InstructorType
 * The keys must map to InstructorTypeItemData properties
 */
export const createFormSchema: AdminBusinessDataFormSchema<InstructorTypeItemData> =
    {
        // the keys must match InstructorDataType properties
        instructor_type: {
            type: AdminBusinessDataFormInputType.Text,
            label: `${INSTRUCTOR_TYPES_DISPLAY_SINGULAR} name`,
            required: true,
            defaultValue: '',
            disabled: false,
            formDataTransform: (
                val: string,
            ): Partial<InstructorTypeItemData> => {
                return { instructor_type: val };
            },
        },
        active: {
            type: AdminBusinessDataFormInputType.StatusRadio,
            label: 'Status',
            required: true,
            defaultValue: true,
            disabled: false,
            formDataTransform: (
                val: 'true' | 'false',
            ): Partial<InstructorTypeItemData> => {
                return { active: parseBoolean(val) };
            },
        },
    };

// The update form schema is the same as the create form schema except the "name" field is disabled
export const updateFormSchema: AdminBusinessDataFormSchema<InstructorTypeItemData> =
    {
        ...createFormSchema,
        instructor_type: {
            ...createFormSchema.instructor_type,
            disabled: true,
        },
    };

const AdminInstructorTypes = () => {
    const dispatch = useDispatch();
    const { addNotification } = useNotifications();

    function useInstructorTypes(): AdminBusinessDataSelectors<InstructorTypeItemData> {
        const dispatch = useDispatch();

        const items: InstructorTypeItemData[] = useSelector(
            selectAllInstructorTypes,
        );
        const itemCount: number = useSelector(selectCount);
        const isLoading: boolean = useSelector(selectIsLoading);
        const isLoaded: boolean = useSelector(selectIsLoaded);
        const error = useSelector(selectError);
        const currentItem: InstructorTypeItemData | null = useSelector(
            selectSelectedInstructorType,
        );
        useEffect(() => {
            dispatch(getInstructorTypesList());
            dispatch(setSelectedInstructorType(null));

            return () => {
                dispatch(resetInstructorTypesSlice());
            };
        }, [dispatch]);
        return { items, itemCount, isLoading, isLoaded, error, currentItem };
    }

    const handleItemSelect = async (instructorType: InstructorTypeItemData) => {
        if (instructorType) {
            dispatch(setSelectedInstructorType(instructorType.instructor_type));
            await dispatch(getInstructorTypesList());
        }
    };

    const handleItemUpdate = async (data: InstructorTypeItemData) => {
        dispatch(setSelectedInstructorType(null));
        const dispatchPromise = dispatch<any>(updateInstructorType(data));
        await handleBusinessDataNotification({
            businessDataType: INSTRUCTOR_TYPES_NAME_KEY,
            businessDataDisplayNameLower:
                INSTRUCTOR_TYPES_DISPLAY_SINGULAR.toLowerCase(),
            itemName: data.instructor_type,
            dispatchPromise: dispatchPromise,
            addNotification: addNotification,
            action: 'update',
        });
    };

    const handleItemCreate = async ({
        instructor_type,
        active,
    }: InstructorTypeItemData) => {
        const dispatchPromise = dispatch<any>(
            addInstructorType(active, instructor_type),
        );
        await handleBusinessDataNotification({
            businessDataType: INSTRUCTOR_TYPES_NAME_KEY,
            businessDataDisplayNameLower:
                INSTRUCTOR_TYPES_DISPLAY_SINGULAR.toLowerCase(),
            itemName: instructor_type,
            dispatchPromise: dispatchPromise,
            addNotification: addNotification,
            action: 'create',
        });
    };

    return (
        <div data-testid="AdminBusinessDataManagementInstructorTypes">
            <AdminBusinessData
                columnDefinitions={columnDefinitions}
                createFormSchema={createFormSchema}
                updateFormSchema={updateFormSchema}
                handleItemCreate={handleItemCreate}
                handleItemSelect={handleItemSelect}
                handleItemUpdate={handleItemUpdate}
                itemDisplayNameSingular={INSTRUCTOR_TYPES_DISPLAY_SINGULAR}
                itemNameKey={INSTRUCTOR_TYPES_NAME_KEY}
                title={INSTRUCTOR_TYPES_TABLE_TITLE}
                useItemList={useInstructorTypes}
            />
        </div>
    );
};

export default AdminInstructorTypes;
